import NextImage from '@allesauto/core/nextImage';
import { BackgroundImages } from '@allesauto/utils/consts';
import { SCREEN_BREAKPOINT, useScreenBreakpoint } from '@allesauto/utils/hooks';
import React, { FC, ReactNode, useLayoutEffect, useRef } from 'react';

import useIsCSR from '@/global/hooks/useIsCSRHook';

export interface UserAccountLayoutProps {
  headerContent: ReactNode;
  footerContent: ReactNode;
  imagePath?: string;
}

const UserAccountLayout: FC<
  React.PropsWithChildren<UserAccountLayoutProps>
> = ({ headerContent, footerContent, imagePath, children }) => {
  const mainContainer = useRef<HTMLDivElement>(null);
  const wrapperContainer = useRef<HTMLDivElement>(null);
  const breakpoint = useScreenBreakpoint();
  const SPACING_BOTTOM = 100;
  const isClientSide = useIsCSR();

  const mobileBreakpoint = breakpoint < SCREEN_BREAKPOINT.MD;

  // fix bg-image jumping bug
  useLayoutEffect(() => {
    if (mainContainer.current && wrapperContainer.current) {
      if (!mobileBreakpoint) {
        const wrapperContainerHeight = wrapperContainer.current.clientHeight;
        const mainContainerHeight = mainContainer.current.clientHeight;
        const mainPaddingTop = parseInt(
          window.getComputedStyle(mainContainer.current).paddingTop
        );

        // calculate the height difference between form and main container
        // (not inlcuding padding top of main container)
        const heightDifference =
          mainContainerHeight - mainPaddingTop - wrapperContainerHeight;

        // if difference is smaller than threshold, form needs extra space
        if (heightDifference <= SPACING_BOTTOM) {
          // calculate new height with extra space
          const newHeight = mainContainerHeight + SPACING_BOTTOM;
          mainContainer.current.style.height = `${newHeight}px`;
        }
      } else {
        // reset height for mobile viewports
        mainContainer.current.style.height = 'auto';
      }
    }
  }, [mobileBreakpoint]);

  return (
    <>
      <div className="h-min w-full">
        {headerContent}
        <main
          ref={mainContainer}
          className="bg-purple80 pt-60px relative md:min-h-screen md:pt-[150px] monochrome:bg-grey90"
        >
          {isClientSide && !mobileBreakpoint && (
            <NextImage
              loader={({ src, width, quality }) => {
                return `${src}?w=${width}&q=${quality || 75}`;
              }}
              alt="Stage Image"
              src={imagePath || BackgroundImages.LOGIN_SCREEN}
              className="absolute inset-0 transform-gpu w-full"
              layout="fill"
              objectFit="cover"
              objectPosition="right center"
              baseUrls={
                process.env.IMAGE_BASE_URL ||
                process.env.NEXT_PUBLIC_IMAGE_BASE_URL ||
                ''
              }
              priority
            />
          )}
          <div ref={wrapperContainer} className="flex-1 h-min md:pb-100px">
            {children}
          </div>
        </main>
        {footerContent}
      </div>
    </>
  );
};

export default UserAccountLayout;
